/**
 * UPDATE - 8/22/2025
 * Reduced mobile suggestions panel vertical size for better screen utilization
 * Changed: Decreased padding, margins, and max-height of suggestions panel
 * Previous: Panel took up too much vertical space on mobile devices
 * Impact: More chat content visible when suggestions panel is open
 * Details: Reduced padding from p-3 to p-1.5, max-height from 240px to 128px
 */

import React, { useState, useEffect, useRef } from 'react';
import { ArrowUp, Loader2, ChevronDown, Sparkles } from 'lucide-react';
import { createTranslator } from '../../../utils/detectLanguage';
import { messageInputPtBR } from '../../mappings/messageInput.pt-br';
import { messageInputES } from '../../mappings/messageInput.es';
import { useChatActions } from '../chatContext';
import { motion, AnimatePresence } from 'framer-motion';
import ChatSuggestionChips from '../../ChatChips';

// Determine which environment to use (default to production)
const isStaging = process.env.REACT_APP_ENVIRONMENT === 'staging';
const isDevelopment = process.env.NODE_ENV === 'development';
console.log("Current environment:", isStaging ? "STAGING" : isDevelopment ? "DEVELOPMENT" : "PRODUCTION");

// Set character limits based on environment
const CHAR_LIMITS = {
  basic: isStaging ? 5000 : 1500,
  pro: 2000,
  admin: 10000
};

const MessageInput = ({
  onSendMessage,
  isInitial,
  disabled,
  tier = 'basic', // Default to basic if not provided
  onSuggestionClick, // Add this to handle suggestion clicks directly
  isHomepage = false,
  isMobile: isMobileProp // Receive from parent for consistency
}) => {
  const [input, setInput] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [isNearLimit, setIsNearLimit] = useState(false);
  // Don't auto-show suggestions on mobile
  const [showSuggestions, setShowSuggestions] = useState(false);
  // Use prop if provided, otherwise fallback to window check
  const [isMobile, setIsMobile] = useState(isMobileProp !== undefined ? isMobileProp : window.innerWidth < 768);
  const textareaRef = useRef(null);
  const suggestionsPanelRef = useRef(null);
  const { pendingContent, clearPendingContent } = useChatActions();

  // Translation helper
  const t = createTranslator({
    'pt-br': messageInputPtBR,
    'es': messageInputES
  });

  // Increase character limit to 800 for mobile basic tier users
  const maxLength = isMobile && tier === 'basic' ? 800 : CHAR_LIMITS[tier];

  // Detect mobile devices - only if not provided by prop
  useEffect(() => {
    if (isMobileProp !== undefined) {
      setIsMobile(isMobileProp);
      return;
    }

    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [isMobileProp]);

  // Handle clicks outside to close suggestions panel
  useEffect(() => {
    if (!isMobile) return; // Only apply on mobile

    const handleClickOutside = (event) => {
      if (
        suggestionsPanelRef.current && 
        !suggestionsPanelRef.current.contains(event.target) &&
        !event.target.closest('[data-suggestions-trigger]')
      ) {
        setShowSuggestions(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobile]);

  useEffect(() => {
    if (pendingContent) {
      setInput(pendingContent);
      clearPendingContent();
      setTimeout(adjustTextareaHeight, 0);
    }
  }, [pendingContent, clearPendingContent]);

  // Auto-focus textarea on mount for immediate typing intent
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }, []);

  useEffect(() => {
    setIsNearLimit(input.length > maxLength * 0.8);
  }, [input, maxLength]);

  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    const scrollHeight = textarea.scrollHeight;
    textarea.style.height = `${Math.min(Math.max(scrollHeight, 42), 120)}px`;
  };

  useEffect(() => {
    adjustTextareaHeight();
  }, [input]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    if (newValue.length < input.length || newValue.length <= maxLength) {
      setInput(newValue);
    }
  };

  const toggleSuggestions = () => {
    setShowSuggestions(prev => !prev);
  };

  const handleSendMessage = () => {
    if (input.trim() && !disabled && input.length <= maxLength) {
      onSendMessage({ 
        sender: "user", 
        text: input
      });
      setInput('');
      if (textareaRef.current) {
        textareaRef.current.style.height = '42px';
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };
  
  const handleSuggestionSelect = (prompt) => {
    // Close the suggestions panel immediately
    setShowSuggestions(false);
    
    // If there's a direct handler, use it
    if (onSuggestionClick) {
      onSuggestionClick(prompt);
    } else {
      // Otherwise, set the input and send immediately
      setInput(prompt);
      
      // Use a small timeout to ensure the state updates
      setTimeout(() => {
        onSendMessage({
          sender: "user",
          text: prompt
        });
      }, 50);
    }
  };

  return (
    <div className={`w-full ${isMobile ? 'px-2' : 'px-4'} ${isMobile ? 'relative' : ''}`}>
      {/* Mobile Suggestions Panel */}
      {isMobile && !isHomepage && (
    <AnimatePresence>
    {showSuggestions && (
      <motion.div
        ref={suggestionsPanelRef}
        data-chat-suggestions="mobile" // Data attribute for onboarding targeting
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 20 }}
        transition={{ duration: 0.2 }}
        className="absolute bottom-full left-0 right-0 mb-2 z-10 px-2"
      >
        <div className="bg-slate-800/95 backdrop-blur-sm border border-emerald-500/20 rounded-lg p-1.5 shadow-lg">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-1">
              <Sparkles className="w-3 h-3 text-emerald-400" />
              <span className="text-[11px] text-emerald-300 font-medium">{t("PickStar Quick AI Suggestions")}</span>
            </div>
            <button
              onClick={toggleSuggestions}
              className="p-0 text-slate-400 hover:text-white transition-colors"
            >
              <ChevronDown className="w-3.5 h-3.5" />
            </button>
          </div>
          
          {/* Divider */}
          <div className="h-px bg-gradient-to-r from-transparent via-emerald-500/20 to-transparent mb-1.5"></div>
          
          {/* Mini-Grid Layout for Suggestions */}
          <div className="suggestions-container max-h-32 overflow-y-auto">
            <ChatSuggestionChips 
              onSuggestionClick={handleSuggestionSelect} 
              variant="default" 
              displayMode="mini-grid" 
            />
          </div>
        </div>
      </motion.div>
    )}
  </AnimatePresence>
)}
    
      {isNearLimit && (
        <>
          {/* Upgrade message above */}
          <motion.div 
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex justify-end mb-1 mr-1"
          >
            <div className="flex items-center gap-1.5 px-2 py-0.5 bg-emerald-500/10 text-emerald-300 rounded text-xs">
              <Sparkles className="w-2.5 h-2.5" />
              <span className="font-light">{t("Upgrade for 2K characters")}</span>
            </div>
          </motion.div>
  
          {/* Remaining characters indicator */}
          <div className="flex justify-end mb-1 mr-1">
            <div className="px-2 py-0.5 text-emerald-300/70 text-xs font-light">
              {(maxLength - input.length).toLocaleString()} {t("remaining")}
            </div>
          </div>
        </>
      )}
  
      <div className={`relative ${isMobile ? 'w-full' : 'max-w-3xl mx-auto'} py-2`}>
        {tier === 'basic' && (
          <div className="absolute -bottom-0.5 left-0 w-full h-0.5 bg-white/5 z-10">
            <motion.div
              className="h-full"
              animate={{ 
                width: `${Math.min((input.length / maxLength) * 100, 100)}%`,
                backgroundColor: input.length > maxLength * 0.8 ? '#ef4444' : '#10b981'
              }}
            />
          </div>
        )}
        
        {/* Input Container */}
        <div className="relative flex items-center rounded-lg border bg-white/5 transition-colors duration-200 overflow-hidden
                      focus-within:border-emerald-500/70 focus-within:bg-white/8
                      hover:bg-white/8
                      border-white/10">
          
          {/* Left Side Tools */}
          <div className="pl-2 pr-1 flex items-center gap-1">
            {/* Mobile: Suggestions Toggle - Only show when input is empty */}
            {isMobile && !input.trim() && (
              <button
                onClick={toggleSuggestions}
                disabled={disabled}
                className={`
                  flex items-center gap-1 px-1.5 py-0.5 rounded-full
                  border transition-all duration-150 text-xs
                  disabled:opacity-30 disabled:cursor-not-allowed
                  ${showSuggestions 
                    ? 'border-emerald-500/50 bg-emerald-500/10 text-emerald-300' 
                    : 'border-white/10 hover:border-white/20 text-white/40 hover:text-white/60'} 
                  active:scale-95`}
                aria-label="Toggle message suggestions"
                data-suggestions-trigger
              >
                <Sparkles className="w-2.5 h-2.5 text-emerald-400" />
                <span className="font-light tracking-tight">{t("Ideas")}</span>
              </button>
            )}
          </div>
          
          {/* Text Area */}
          <textarea
            ref={textareaRef}
            data-chat-message-input="true" // Data attribute for onboarding targeting
            value={input}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            placeholder={t('Ask Away!')}
            disabled={disabled}
            rows={1}
            className="flex-1 py-2.5 pr-2 pl-1 bg-transparent text-white font-light tracking-wide text-sm prevent-zoom placeholder-white/30 resize-none focus:outline-none scrollbar-thin scrollbar-thumb-emerald-500/30 scrollbar-thumb-rounded scrollbar-track-transparent"
            style={{
              minHeight: '42px',
              maxHeight: '120px',
              lineHeight: '1.5',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              fontSize: isMobile ? '16px' : undefined // Add this line
            }}
          />
          
          {/* Send Button - Right Side */}
          <div className="px-2">
            <button 
              onClick={handleSendMessage}
              disabled={disabled || !input.trim() || input.length > maxLength}
              className={`
                p-1.5
                rounded-md
                transition-all
                duration-150
                flex
                items-center
                justify-center
                ${input.trim() && input.length <= maxLength
                  ? 'bg-emerald-600 hover:bg-emerald-500 text-white active:scale-95' 
                  : 'text-white/30'}
                disabled:opacity-30
                disabled:cursor-not-allowed
              `}
              aria-label="Send message"
            >
              {disabled ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <ArrowUp className="w-4 h-4" />
              )}
            </button>
          </div>
        </div>
      </div>
      
      {/* Caution message at the bottom of the message bar */}
      <div className="text-[10px] text-gray-400 text-center mt-1">
        {t("AI analysis offer insights, not guarantees—use responsibly.")}
      </div>
      
      {/* Inline styles for suggestions container */}
      <style>{`
        .suggestions-container {
          max-height: 40vh;
          overflow-y: auto;
          -webkit-overflow-scrolling: touch;
        }
        .suggestions-container::-webkit-scrollbar {
          width: 2px;
        }
        .suggestions-container::-webkit-scrollbar-thumb {
          background: rgba(52, 211, 153, 0.3);
          border-radius: 10px;
        }
        
        @media (max-width: 767px) {
          .prevent-zoom {
            font-size: 16px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default MessageInput;